import React, { useState } from 'react';
import { Button, Dropdown, Menu, message, Input, Select } from 'antd';
import { plansApi } from '../../api/plans';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { Buffer } from 'buffer';
import PlanPdfTemplate from './PlanPdfTemplate';
import './PlanControls.css';

// Add this line before your component definition
window.Buffer = window.Buffer || Buffer;

function PlanControls({
	plan,
	orgData,
	onPlanUpdate,
	isEditMode,
	setIsEditMode,
	onSave,
	isDirty,
}) {
	// Add state for title editing
	const [isEditingTitle, setIsEditingTitle] = useState(false);
	const [editedTitle, setEditedTitle] = useState(plan.mainTitle);

	// Add state for status selection
	const [selectedStatus, setSelectedStatus] = useState(plan.status);

	const handleAddClick = ({ key }) => {
		switch (key) {
			case 'section':
				// Add empty section at the end
				const newSection = {
					id: `temp_${Date.now()}`, // Temporary ID until saved
					title: 'New Section',
					description: '',
					estimatedDuration: 0,
					order: (plan.sections?.length || 0) + 1,
					blocks: [],
				};
				onPlanUpdate({
					...plan,
					sections: [...(plan.sections || []), newSection],
				});
				break;
			case 'block':
				// We'll implement this when we add the section selection modal
				break;
		}
	};

	const handleSaveClick = async () => {
		try {
			await onSave();
		} catch (error) {
			console.error('Error saving plan:', error);
			message.error('Failed to save plan');
		}
	};

	// Handle title update
	const handleTitleUpdate = async () => {
		try {
			const updatedPlan = await plansApi.update(plan.id, {
				...plan,
				mainTitle: editedTitle,
			});
			onPlanUpdate(updatedPlan.plan);
			setIsEditingTitle(false);
		} catch (error) {
			console.error('Error updating plan title:', error);
			message.error('Failed to update plan title');
		}
	};

	// Handle status update
	const handleStatusChange = async (newStatus) => {
		try {
			const updatedPlan = await plansApi.update(plan.id, {
				...plan,
				status: newStatus,
			});
			onPlanUpdate(updatedPlan.plan);
			setSelectedStatus(newStatus);
		} catch (error) {
			console.error('Error updating plan status:', error);
			message.error('Failed to update plan status');
		}
	};

	const addMenu = (
		<Menu onClick={handleAddClick}>
			<Menu.Item key='section'>Add Section</Menu.Item>
			<Menu.Item key='block'>Add Block</Menu.Item>
		</Menu>
	);

	const ExportButton = () => {
		if (!isEditMode) {
			return (
				<PDFDownloadLink
					document={
						<PlanPdfTemplate
							plan={plan}
							orgData={orgData}
						/>
					}
					fileName={`${plan.mainTitle}.pdf`}>
					{({ blob, url, loading, error }) => (
						<Button
							icon={<span className='material-icons'>print</span>}
							disabled={loading}>
							{loading ? 'Generating PDF...' : 'Export'}
						</Button>
					)}
				</PDFDownloadLink>
			);
		}
	};

	return (
		<div className='plan-controls'>
			<div className='plan-info'>
				{isEditMode && isEditingTitle ? (
					<div className='title-edit-container'>
						<Input
							value={editedTitle}
							onChange={(e) => setEditedTitle(e.target.value)}
							onPressEnter={handleTitleUpdate}
							onBlur={handleTitleUpdate}
							autoFocus
						/>
					</div>
				) : (
					<h1
						onClick={() => isEditMode && setIsEditingTitle(true)}
						style={{ cursor: isEditMode ? 'pointer' : 'default' }}>
						{plan.mainTitle}
						{isEditMode && (
							<span className='material-icons edit-icon'>
								edit
							</span>
						)}
					</h1>
				)}

				<Select
					value={selectedStatus}
					onChange={handleStatusChange}
					style={{ width: 120 }}
					disabled={!isEditMode}>
					<Select.Option value='draft'>Draft</Select.Option>
					<Select.Option value='published'>Published</Select.Option>
					<Select.Option value='archived'>Archived</Select.Option>
				</Select>
			</div>

			<div className='plan-actions'>
				{isEditMode ? (
					<>
						<Dropdown
							overlay={addMenu}
							placement='bottomRight'>
							<Button
								type='primary'
								icon={
									<span className='material-icons'>
										add_circle
									</span>
								}>
								Add
							</Button>
						</Dropdown>
						<Button
							type='primary'
							onClick={handleSaveClick}
							icon={
								<span className='material-icons'>
									{isDirty ? 'save' : 'cancel'}
								</span>
							}>
							{isDirty ? 'Save Changes' : 'Cancel'}
						</Button>
					</>
				) : (
					<Button
						onClick={() => setIsEditMode(true)}
						icon={<span className='material-icons'>edit</span>}>
						Edit Plan
					</Button>
				)}
				<ExportButton />
			</div>
		</div>
	);
}

export default PlanControls;
